import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Table,
  Space,
  Row,
  Col,
  Statistic,
  Modal,
  InputNumber,
  message,
  ConfigProvider,
  Tag,
  Dropdown,
  MenuProps,
  Grid,
} from "antd";
import type { TableProps } from "antd";
import locale from "antd/es/date-picker/locale/zh_CN";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { payment } from "../../utils/payment";
import dayjs from "dayjs";
import zhCN from "antd/locale/zh_CN";
import "dayjs/locale/zh-cn";
import RevenueChart from "../../componets/charts/RevenueChart";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// 设置 dayjs 的默认语言为中文
dayjs.locale("zh-cn");

interface OrderItem {
  id: number;
  order_id: string;
  open_id: number;
  order_amount: number;
  pay_amount: number;
  discount_amount: number;
  pay_type: string;
  pay_status: number;
  order_subject: string;
  product_code: string;
  product_count: number;
  created_at: string;
  pay_time: string;
  refund_amount: number;
  refund_reason: string;
  refund_status: number;
  refund_time: string;
  remarks: string;
}

interface OrderListResponse {
  code: number;
  data: {
    list: OrderItem[];
    total: number;
  };
  msg: string;
}

interface PayInfoResponse {
  code: number;
  data: {
    average_amount: number;
    completed_orders: number;
    refund_amount: number;
    refunded_orders: number;
    success_rate: number;
    total_amount: number;
    total_orders: number;
    success_pay_amount: number;
    total_completed_orders: number;
    total_completed_pay_amount: number;
    wechat_pay_amount?: number;
    alipay_pay_amount?: number;
  };
  msg: string;
}

const OrderManagement: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paginationRef = React.useRef(pagination);
  const [currentFilter, setCurrentFilter] = useState<Record<string, any>>({});
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [refundReason, setRefundReason] = useState("");
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [currentMonthStats, setCurrentMonthStats] = useState<
    PayInfoResponse["data"] | null
  >(null);
  const [lastMonthStats, setLastMonthStats] = useState<
    PayInfoResponse["data"] | null
  >(null);
  const [totalStats, setTotalStats] = useState<PayInfoResponse["data"] | null>(
    null
  );
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [revenueChartVisible, setRevenueChartVisible] = useState(false);
  const [revenueChartDateRange, setRevenueChartDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs]
  >([dayjs().subtract(29, "day"), dayjs()]);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 更新 ref 当 pagination 改变时
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const fetchPayInfo = useCallback(async () => {
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const currentMonthStart = `${currentYear}-${String(currentMonth).padStart(
        2,
        "0"
      )}-01`;
      const currentMonthEnd = `${currentYear}-${String(currentMonth).padStart(
        2,
        "0"
      )}-${new Date(currentYear, currentMonth, 0).getDate()}`;

      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;
      const lastMonthStart = `${lastYear}-${String(lastMonth).padStart(
        2,
        "0"
      )}-01`;
      const lastMonthEnd = `${lastYear}-${String(lastMonth).padStart(
        2,
        "0"
      )}-${new Date(lastYear, lastMonth, 0).getDate()}`;

      const currentMonthResponse = await request.get<PayInfoResponse>(
        `${API_BASE_URL}/api/v3/admin/orderList/payInfo`,
        {
          params: {
            start_time: currentMonthStart,
            end_time: currentMonthEnd,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const lastMonthResponse = await request.get<PayInfoResponse>(
        `${API_BASE_URL}/api/v3/admin/orderList/payInfo`,
        {
          params: {
            start_time: lastMonthStart,
            end_time: lastMonthEnd,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const totalResponse = await request.get<PayInfoResponse>(
        `${API_BASE_URL}/api/v3/admin/orderList/payInfo`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (currentMonthResponse.data.code === 200) {
        setCurrentMonthStats(currentMonthResponse.data.data);
      }
      if (lastMonthResponse.data.code === 200) {
        setLastMonthStats(lastMonthResponse.data.data);
      }
      if (totalResponse.data.code === 200) {
        setTotalStats(totalResponse.data.data);
      }
    } catch (error) {
      message.error("获取统计信息失败");
    }
  }, [API_BASE_URL, token]);

  const fetchOrders = useCallback(
    async (params?: { current?: number; pageSize?: number }) => {
      setLoading(true);
      try {
        const response = await request.get<OrderListResponse>(
          `${API_BASE_URL}/api/v3/admin/orderList`,
          {
            params: {
              page: params?.current || paginationRef.current.current,
              page_size: params?.pageSize || paginationRef.current.pageSize,
              ...currentFilter,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          setOrders(response.data.data.list);
          setTotal(response.data.data.total);
          setPagination((prev) => ({
            ...prev,
            current: params?.current || prev.current,
            pageSize: params?.pageSize || prev.pageSize,
          }));
        } else {
          message.error(response.data.msg || "获取订单列表失败");
        }
      } catch (error) {
        message.error("获取订单列表失败");
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, token, currentFilter]
  );

  useEffect(() => {
    fetchOrders();
    fetchPayInfo();
  }, [fetchOrders, fetchPayInfo]);

  // 发送订单统计报告
  const sendOrderReport = async (timeRange?: {
    start: string;
    end: string;
  }) => {
    setSendingReport(true);
    try {
      const params: any = {};

      if (timeRange) {
        params.start_time = timeRange.start;
        params.end_time = timeRange.end;
      } else {
        // 默认使用上个月
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;

        params.start_time = `${lastYear}-${String(lastMonth).padStart(
          2,
          "0"
        )}-01`;
        params.end_time = `${lastYear}-${String(lastMonth).padStart(
          2,
          "0"
        )}-${new Date(lastYear, lastMonth, 0).getDate()}`;
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: any;
      }>(`${API_BASE_URL}/api/v3/admin/orderList/sendReport`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        message.success("订单统计报告发送成功");
      } else {
        message.error(response.data.msg || "发送报告失败");
      }
    } catch (error: any) {
      console.error("发送订单统计报告失败:", error);
      message.error(error.response?.data?.msg || "发送报告失败，请稍后重试");
    } finally {
      setSendingReport(false);
    }
  };

  // 处理报告发送菜单点击
  const handleReportMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "last_month") {
      // 发送上个月报告（默认）
      sendOrderReport();
    } else if (key === "current_month") {
      // 发送当前月报告
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const start = `${currentYear}-${String(currentMonth).padStart(
        2,
        "0"
      )}-01`;
      const end = `${currentYear}-${String(currentMonth).padStart(
        2,
        "0"
      )}-${new Date(currentYear, currentMonth, 0).getDate()}`;

      sendOrderReport({ start, end });
    } else if (key === "custom") {
      // 显示自定义时间范围选择器
      // 用闭包变量接收选择结果,替代此前 getElementById 读 DOM value 的反模式。
      let reportRange: [string, string] | null = null;
      Modal.confirm({
        title: "选择报告时间范围",
        content: (
          <div style={{ marginTop: 16 }}>
            <RangePicker
              style={{ width: "100%" }}
              locale={locale}
              placeholder={["开始日期", "结束日期"]}
              format="YYYY-MM-DD"
              onChange={(_, dateStrings) => {
                reportRange =
                  dateStrings[0] && dateStrings[1]
                    ? [dateStrings[0], dateStrings[1]]
                    : null;
              }}
            />
          </div>
        ),
        onOk: () => {
          if (!reportRange) {
            message.error("请选择时间范围");
            return Promise.reject();
          }
          sendOrderReport({ start: reportRange[0], end: reportRange[1] });
        },
      });
    }
  };

  // 处理统计卡片点击
  const handleStatCardClick = (type: "current" | "last" | "total") => {
    let dateRange: [dayjs.Dayjs, dayjs.Dayjs];

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (type === "current") {
      // 本月
      dateRange = [
        dayjs(`${currentYear}-${String(currentMonth).padStart(2, "0")}-01`),
        dayjs().endOf("month"),
      ];
    } else if (type === "last") {
      // 上月
      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;
      dateRange = [
        dayjs(`${lastYear}-${String(lastMonth).padStart(2, "0")}-01`),
        dayjs(`${lastYear}-${String(lastMonth).padStart(2, "0")}-01`).endOf(
          "month"
        ),
      ];
    } else {
      // 总业务（最近30天）
      dateRange = [dayjs().subtract(29, "day"), dayjs()];
    }

    setRevenueChartDateRange(dateRange);
    setRevenueChartVisible(true);
  };

  // 报告发送菜单
  const reportMenuItems: MenuProps["items"] = [
    {
      key: "last_month",
      label: "发送上月报告",
      icon: <i className="ri-calendar-check-line" />,
    },
    {
      key: "current_month",
      label: "发送本月报告",
      icon: <i className="ri-calendar-line" />,
    },
    {
      key: "custom",
      label: "自定义时间范围",
      icon: <i className="ri-calendar-event-line" />,
    },
  ];

  const handleRefund = async () => {
    if (!selectedOrder) return;

    Modal.confirm({
      title: "确认退款",
      content: (
        <div>
          <p>确定要对以下订单进行退款吗？</p>
          <p>订单号：{selectedOrder.order_id}</p>
          <p>商品名称：{selectedOrder.order_subject}</p>
          <p>退款金额：¥{refundAmount.toFixed(2)}</p>
          <p>退款原因：{refundReason}</p>
        </div>
      ),
      okText: "确认退款",
      cancelText: "取消",
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        setSubmittingRefund(true);
        try {
          // 使用统一退款接口
          const refundResult = await payment.refundOrder(
            {
              order_id: selectedOrder.order_id,
              refund_amount: refundAmount, // 直接使用元为单位
              refund_reason: refundReason,
            },
            token
          );

          if (refundResult && refundResult.success) {
            message.success(`退款成功：${refundResult.message}`);
            setRefundModalVisible(false);
            setSelectedOrder(null);
            setRefundAmount(0);
            setRefundReason("");
            fetchOrders(); // 刷新订单列表
          } else {
            message.error("退款失败");
          }
        } catch (error) {
          console.error("退款失败:", error);
          message.error("退款失败，请重试");
        } finally {
          setSubmittingRefund(false);
        }
      },
    });
  };

  const columns: TableProps<OrderItem>["columns"] = [
    {
      title: "订单号",
      dataIndex: "order_id",
      key: "order_id",
      width: isMobile ? 120 : undefined,
      ellipsis: isMobile,
    },
    {
      title: "用户",
      dataIndex: "open_id",
      key: "open_id",
      width: isMobile ? 80 : undefined,
    },
    {
      title: "商品",
      dataIndex: "order_subject",
      key: "order_subject",
      ellipsis: isMobile,
    },
    {
      title: "数量",
      dataIndex: "product_count",
      key: "product_count",
      width: isMobile ? 60 : undefined,
    },
    {
      title: "订单金额",
      dataIndex: "order_amount",
      key: "order_amount",
      render: (amount: number) => `¥${(amount / 100).toFixed(2)}`,
      width: isMobile ? 80 : undefined,
    },
    {
      title: "实付金额",
      dataIndex: "pay_amount",
      key: "pay_amount",
      render: (amount: number) => `¥${(amount / 100).toFixed(2)}`,
      width: isMobile ? 80 : undefined,
    },
    {
      title: "支付方式",
      dataIndex: "pay_type",
      key: "pay_type",
      render: (payType: string) => {
        const payTypeMap: Record<string, { text: string; color: string }> = {
          alipay: { text: "支付宝", color: "blue" },
          wechat: { text: "微信支付", color: "green" },
          code: { text: "兑换码", color: "orange" },
          reward: { text: "活动奖品", color: "red" },
        };
        const { text, color } = payTypeMap[payType] || {
          text: payType,
          color: "default",
        };
        return (
          <Tag color={color}>
            {isMobile ? payTypeMap[payType]?.text.slice(0, 2) : text}
          </Tag>
        );
      },
      width: isMobile ? 70 : undefined,
    },
    {
      title: "支付状态",
      dataIndex: "pay_status",
      key: "pay_status",
      render: (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
          0: { text: "未支付", color: "#ff4d4f" },
          1: { text: "已支付", color: "#52c41a" },
          2: { text: "已退款", color: "#faad14" },
        };
        const { text, color } = statusMap[status] || {
          text: "未知",
          color: "#999",
        };
        return (
          <span style={{ color, whiteSpace: "nowrap" }}>
            {isMobile ? text.slice(0, 2) : text}
          </span>
        );
      },
      width: isMobile ? 70 : undefined,
    },
    !isMobile && {
      title: "支付时间",
      dataIndex: "pay_time",
      key: "pay_time",
      render: (time: string) => {
        return time && time !== "0001-01-01T00:00:00Z"
          ? new Date(time).toLocaleString("zh-CN")
          : "-";
      },
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: React.SetStateAction<OrderItem | null>) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedOrder(record);
            setIsDetailModalVisible(true);
          }}
          style={{ padding: 0 }}
          size={isMobile ? "small" : "middle"}
        >
          查看
        </Button>
      ),
      width: isMobile ? 50 : undefined,
    },
  ].filter(Boolean) as TableProps<OrderItem>["columns"];

  const handleSearch = (values: any) => {
    const { timeRange, ...rest } = values;

    const params = {
      ...rest,
      start_time: timeRange?.[0]?.format("YYYY-MM-DD"),
      end_time: timeRange?.[1]?.format("YYYY-MM-DD"),
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === "") {
        delete params[key];
      }
    });

    setCurrentFilter(params);
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    fetchOrders(params);
  };

  const orderDetailModal = (
    <Modal
      getContainer={false}
      title="订单详情"
      open={isDetailModalVisible}
      onCancel={() => {
        setIsDetailModalVisible(false);
        setSelectedOrder(null);
      }}
      footer={[
        <Button
          key="close"
          onClick={() => {
            setIsDetailModalVisible(false);
            setSelectedOrder(null);
          }}
        >
          关闭
        </Button>,
        selectedOrder?.pay_status === 1 && (
          <Button
            key="refund"
            type="primary"
            danger
            onClick={() => {
              setRefundModalVisible(true);
              setRefundAmount(selectedOrder.pay_amount / 100);
            }}
          >
            申请退款
          </Button>
        ),
      ]}
      width={isMobile ? "95%" : 700}
    >
      {selectedOrder && (
        <div style={{ padding: "8px 0" }}>
          <h3
            style={{
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: "8px",
              marginBottom: "16px",
            }}
          >
            基本信息
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <p>
                <strong>订单号：</strong>
                {selectedOrder.order_id}
              </p>
              <p>
                <strong>用户UID：</strong>
                {selectedOrder.open_id}
              </p>
              <p>
                <strong>商品名称：</strong>
                {selectedOrder.order_subject}
              </p>
              <p>
                <strong>商品代码：</strong>
                {selectedOrder.product_code}
              </p>
              <p>
                <strong>购买数量：</strong>
                {selectedOrder.product_count}
              </p>
            </div>
            <div>
              <p>
                <strong>订单金额：</strong>¥
                {(selectedOrder.order_amount / 100).toFixed(2)}
              </p>
              <p>
                <strong>实付金额：</strong>¥
                {(selectedOrder.pay_amount / 100).toFixed(2)}
              </p>
              <p>
                <strong>优惠金额：</strong>¥
                {(selectedOrder.discount_amount / 100).toFixed(2)}
              </p>
              <p>
                <strong>支付方式：</strong>
                <Tag
                  color={
                    selectedOrder.pay_type === "alipay"
                      ? "blue"
                      : selectedOrder.pay_type === "wechat"
                      ? "green"
                      : selectedOrder.pay_type === "code"
                      ? "orange"
                      : "red"
                  }
                >
                  {selectedOrder.pay_type === "alipay"
                    ? "支付宝"
                    : selectedOrder.pay_type === "wechat"
                    ? "微信支付"
                    : selectedOrder.pay_type === "code"
                    ? "兑换码"
                    : "活动奖品"}
                </Tag>
              </p>
              <p>
                <strong>支付状态：</strong>
                <Tag
                  color={
                    selectedOrder.pay_status === 1
                      ? "#52c41a"
                      : selectedOrder.pay_status === 2
                      ? "#faad14"
                      : "#ff4d4f"
                  }
                >
                  {selectedOrder.pay_status === 1
                    ? "已支付"
                    : selectedOrder.pay_status === 2
                    ? "已退款"
                    : "未支付"}
                </Tag>
              </p>
            </div>
          </div>

          <h3
            style={{
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 0 8px",
              marginBottom: "16px",
            }}
          >
            时间信息
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <p>
                <strong>创建时间：</strong>
                {new Date(selectedOrder.created_at).toLocaleString("zh-CN")}
              </p>
              <p>
                <strong>支付时间：</strong>
                {selectedOrder.pay_time &&
                selectedOrder.pay_time !== "0001-01-01T00:00:00Z"
                  ? new Date(selectedOrder.pay_time).toLocaleString("zh-CN")
                  : "-"}
              </p>
            </div>
            <div>
              <p>
                <strong>退款时间：</strong>
                {selectedOrder.refund_time &&
                selectedOrder.refund_time !== "0001-01-01T00:00:00Z"
                  ? new Date(selectedOrder.refund_time).toLocaleString("zh-CN")
                  : "-"}
              </p>
            </div>
          </div>

          {selectedOrder.refund_status > 0 && (
            <>
              <h3
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  padding: "16px 0 8px",
                  marginBottom: "16px",
                }}
              >
                退款信息
              </h3>
              <div>
                <p>
                  <strong>退款金额：</strong>¥
                  {(selectedOrder.refund_amount / 100).toFixed(2)}
                </p>
                <p>
                  <strong>退款原因：</strong>
                  {selectedOrder.refund_reason || "-"}
                </p>
                <p>
                  <strong>退款状态：</strong>
                  <span
                    style={{
                      color:
                        selectedOrder.refund_status === 2
                          ? "#52c41a"
                          : selectedOrder.refund_status === 1
                          ? "#faad14"
                          : selectedOrder.refund_status === 3
                          ? "#ff4d4f"
                          : "#999",
                    }}
                  >
                    {selectedOrder.refund_status === 0
                      ? "未退款"
                      : selectedOrder.refund_status === 1
                      ? "退款中"
                      : selectedOrder.refund_status === 2
                      ? "已退款"
                      : selectedOrder.refund_status === 3
                      ? "退款失败"
                      : "未知状态"}
                  </span>
                </p>
              </div>
            </>
          )}

          <h3
            style={{
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 0 8px",
              marginBottom: "16px",
            }}
          >
            备注信息
          </h3>

          <p
            style={{
              padding: "16px",
              background: "#f5f5f5",
              borderRadius: 8,
              whiteSpace: "pre-wrap",
            }}
          >
            {selectedOrder.remarks || "-"}
          </p>
        </div>
      )}
    </Modal>
  );

  return (
    <ConfigProvider locale={zhCN}>
      <div
        style={{
          background: "#f0f2f5",
          minHeight: "100%",
          borderRadius: 8,
          padding: isMobile ? "8px" : "16px",
        }}
      >
        <Row gutter={[8, 8]}>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("current")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    本月订单数量 <i className="ri-shopping-cart-2-line" />
                  </>
                }
                value={currentMonthStats?.completed_orders || 0}
                suffix="单"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("current")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    本月订单金额 <i className="ri-money-cny-circle-line" />
                  </>
                }
                value={(currentMonthStats?.total_amount || 0) / 100}
                precision={2}
                prefix="¥"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("last")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    上月订单数量 <i className="ri-shopping-cart-2-line" />
                  </>
                }
                value={lastMonthStats?.total_orders || 0}
                suffix="单"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("last")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    上月订单金额{" "}
                    <i className="ri-money-cny-circle-line" />
                    {(lastMonthStats?.wechat_pay_amount != null ||
                      lastMonthStats?.alipay_pay_amount != null) && (
                      <span style={{ fontWeight: 400, fontSize: 12 }}>
                        （微信：¥
                        {((lastMonthStats?.wechat_pay_amount ?? 0) / 100).toFixed(
                          2
                        )}{" "}
                        支付宝：¥
                        {((lastMonthStats?.alipay_pay_amount ?? 0) / 100).toFixed(
                          2
                        )}
                        ）
                      </span>
                    )}
                  </>
                }
                value={(lastMonthStats?.total_amount || 0) / 100}
                precision={2}
                prefix="¥"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("total")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    总业务订单数 <i className="ri-file-list-3-line" />
                  </>
                }
                value={totalStats?.total_orders || 0}
                suffix="单"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={8}>
            <Card
              style={{ height: "100%", cursor: "pointer" }}
              variant="outlined"
              onClick={() => handleStatCardClick("total")}
              hoverable
            >
              <Statistic
                title={
                  <>
                    总业务金额 <i className="ri-funds-line" />
                  </>
                }
                value={(totalStats?.total_amount || 0) / 100}
                precision={2}
                prefix="¥"
                valueStyle={{ fontSize: isMobile ? 16 : 24 }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: isMobile ? 14 : 16 }}>
                <i
                  className="ri-filter-3-line"
                  style={{ marginRight: "8px", color: "#1677ff" }}
                />
                筛选条件
              </span>
              {!isMobile && (
                <Dropdown
                  menu={{
                    items: reportMenuItems,
                    onClick: handleReportMenuClick,
                  }}
                  placement="bottomRight"
                >
                  <Button
                    type="primary"
                    loading={sendingReport}
                    icon={<i className="ri-mail-send-line" />}
                    size={isMobile ? "small" : "middle"}
                  >
                    发送统计报告
                    <i
                      className="ri-arrow-down-s-line"
                      style={{ marginLeft: 4 }}
                    />
                  </Button>
                </Dropdown>
              )}
            </div>
          }
          style={{ marginTop: "16px", marginBottom: "16px" }}
          variant="outlined"
        >
          <Form form={form} layout="vertical" onFinish={handleSearch}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="order_id" label="订单号">
                  <Input
                    placeholder="请输入订单号"
                    size={isMobile ? "small" : "middle"}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item name="product_code" label="类型" initialValue="">
                  <Select
                    placeholder="请选择类型"
                    size={isMobile ? "small" : "middle"}
                  >
                    <Select.Option value="">所有</Select.Option>
                    <Select.Option value="P">PostChat</Select.Option>
                    <Select.Option value="S">摘要AI</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item name="pay_type" label="支付方式" initialValue="">
                  <Select
                    placeholder="请选择支付方式"
                    size={isMobile ? "small" : "middle"}
                  >
                    <Select.Option value="">所有</Select.Option>
                    <Select.Option value="alipay">支付宝</Select.Option>
                    <Select.Option value="wechat">微信支付</Select.Option>
                    <Select.Option value="code">兑换码</Select.Option>
                    <Select.Option value="reward">活动奖品</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item name="pay_status" label="支付状态" initialValue={-1}>
                  <Select
                    placeholder="请选择支付状态"
                    size={isMobile ? "small" : "middle"}
                  >
                    <Select.Option value={-1}>所有</Select.Option>
                    <Select.Option value={1}>已支付</Select.Option>
                    <Select.Option value={0}>未支付</Select.Option>
                    <Select.Option value={2}>已退款</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item name="open_id" label="用户UID">
                  <Input
                    placeholder="请输入用户UID"
                    size={isMobile ? "small" : "middle"}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item name="timeRange" label="支付时间">
                  <RangePicker
                    style={{ width: "100%" }}
                    locale={locale}
                    placeholder={["开始日期", "结束日期"]}
                    size={isMobile ? "small" : "middle"}
                    showTime={
                      !isMobile
                        ? {
                            format: "HH:mm",
                            defaultValue: [
                              dayjs("00:00:00", "HH:mm:ss"),
                              dayjs("23:59:59", "HH:mm:ss"),
                            ],
                            hideDisabledOptions: true,
                            showNow: true,
                          }
                        : false
                    }
                    format={isMobile ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm"}
                    presets={
                      !isMobile
                        ? [
                            {
                              label: "今天",
                              value: [
                                dayjs().startOf("day"),
                                dayjs().endOf("day"),
                              ],
                            },
                            {
                              label: "本周",
                              value: [
                                dayjs().startOf("week"),
                                dayjs().endOf("week"),
                              ],
                            },
                            {
                              label: "本月",
                              value: [
                                dayjs().startOf("month"),
                                dayjs().endOf("month"),
                              ],
                            },
                            {
                              label: "上个月",
                              value: [
                                dayjs().subtract(1, "month").startOf("month"),
                                dayjs().subtract(1, "month").endOf("month"),
                              ],
                            },
                          ]
                        : undefined
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={6}>
                <Form.Item label=" " colon={false}>
                  <Space wrap>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      size={isMobile ? "small" : "middle"}
                    >
                      查询
                    </Button>
                    <Button
                      onClick={() => {
                        form.resetFields();
                        fetchOrders();
                      }}
                      size={isMobile ? "small" : "middle"}
                    >
                      重置
                    </Button>
                    {isMobile && (
                      <Dropdown
                        menu={{
                          items: reportMenuItems,
                          onClick: handleReportMenuClick,
                        }}
                        placement="bottomRight"
                      >
                        <Button
                          type="primary"
                          loading={sendingReport}
                          icon={<i className="ri-mail-send-line" />}
                          size="small"
                        >
                          报告
                        </Button>
                      </Dropdown>
                    )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card
          title={
            <span style={{ fontSize: isMobile ? 14 : 16 }}>
              <i
                className="ri-file-list-3-line"
                style={{ marginRight: "8px", color: "#1677ff" }}
              ></i>
              订单列表
            </span>
          }
          variant="outlined"
          styles={{
            body: { padding: isMobile ? 4 : 8 },
          }}
        >
          <Table<OrderItem>
            columns={columns}
            dataSource={orders}
            rowKey="id"
            loading={loading}
            size={isMobile ? "small" : "middle"}
            scroll={{ x: isMobile ? 800 : undefined }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
              showSizeChanger: !isMobile,
              showQuickJumper: !isMobile,
              showTotal: (total) => `共 ${total} 条记录`,
              size: isMobile ? "small" : "default",
              onChange: (page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize,
                }));
                fetchOrders({
                  current: page,
                  pageSize: pageSize,
                });
              },
            }}
          />
        </Card>

        {orderDetailModal}
        <Modal
          title="订单退款"
          open={refundModalVisible}
          onOk={handleRefund}
          onCancel={() => {
            setRefundModalVisible(false);
            setRefundAmount(0);
            setRefundReason("");
          }}
          okText="确认退款"
          cancelText="取消"
          confirmLoading={submittingRefund}
          width={isMobile ? "95%" : undefined}
        >
          <div style={{ marginBottom: 16 }}>
            <p>订单号：{selectedOrder?.order_id}</p>
            <p>商品名称：{selectedOrder?.order_subject}</p>
            <p>
              实付金额：¥
              {selectedOrder
                ? (selectedOrder.pay_amount / 100).toFixed(2)
                : "0.00"}
            </p>
          </div>
          <Form layout="vertical">
            <Form.Item
              label="退款金额"
              required
              help="退款金额不能超过实付金额"
            >
              <InputNumber
                value={refundAmount}
                onChange={(value) => setRefundAmount(value || 0)}
                min={0}
                max={selectedOrder ? selectedOrder.pay_amount / 100 : 0}
                precision={2}
                prefix="¥"
                style={{ width: "100%" }}
                size={isMobile ? "small" : "middle"}
              />
            </Form.Item>
            <Form.Item label="退款原因" required help="请简要说明退款原因">
              <Input.TextArea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="请输入退款原因"
                rows={3}
                size={isMobile ? "small" : "middle"}
              />
            </Form.Item>
          </Form>
        </Modal>

        <RevenueChart
          visible={revenueChartVisible}
          onCancel={() => setRevenueChartVisible(false)}
          defaultDateRange={revenueChartDateRange}
        />
      </div>
    </ConfigProvider>
  );
};

export default OrderManagement;
